import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import {
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    SuperAdminTenantUserCreateFragment, 
    SuperAdminTenantUserCreateSchema, 
    SuperAdminTenantUserUpdateFragment, 
    SuperAdminTenantUserUpdateSchema 
} from "src/components/superadmin/tenants/users/SuperAdminTenantUserSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = SuperAdminTenantUserCreateFragment

let initialState: FormPayloadType = {
    role: 'agency_operations_administrator' as any,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateSuperAdminTenantUserDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    const { currentItemId: currentTenantId } = useAppState(state=>state.tenants)
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({ ...payload, tenant_id: currentTenantId })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={SuperAdminTenantUserCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateSuperAdminTenantUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add User'
            action={CreateSuperAdminTenantUserDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: SuperAdminTenantUserUpdateFragment = {
    status: User_Status_Enum.Active,
    role: 'agency_operations_administrator' as any,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: "",
    timezone: 'America/New_York'
};

export const UpdateSuperAdminTenantUserDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: SuperAdminTenantUserUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<SuperAdminTenantUserUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<SuperAdminTenantUserUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={SuperAdminTenantUserUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateSuperAdminTenantUserAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateSuperAdminTenantUserDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditSuperAdminTenantUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateSuperAdminTenantUserDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}